import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Button, Modal, ModalYesNo, Select } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { Switch } from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  useSaveAppointmentProfile,
  type SaveAppointmentProfileBody,
  type SaveAppointmentProfileEntry,
} from '@/settings/services';
import type {
  AppointmentProfileEntry,
  ApptLocation,
} from '@/types/settings-api';
import { flutterColorToCss, cssColorToFlutter } from './appointment-profile-color';

/**
 * Appointment Profile editor — PROD-762 (FINAL editor of the epic).
 *
 * Flutter parity: `edit_appointment_profile_widget.dart`. Edits a
 * single profile entry; submits the FULL practitioner profile list to
 * `POST /api/Doctor/AppointmentProfile` with `isUpdated: true` on
 * the edited row and `false` on others. Deletions go into the
 * `isDeleted: string[]` array.
 *
 * SCOPE TRADEOFF: this is the largest body shape on the Schedule tab
 * (23 fields with nested envelopes). Editor focuses on the most-used
 * surface fields (name, duration, location, type, color, in-app /
 * default / preferred toggles). Practice + speciality + room +
 * reasonCode + billingProfile are preserved verbatim from the source
 * row on Edit (and require Add-mode selectors); editing those
 * sub-relations is left for follow-up PRs to avoid making this
 * dialog enormous. Mirrors Flutter's approach where these fields are
 * either chosen at creation or have dedicated edit surfaces.
 */

const COLOR_PRESETS: { value: string; label: string }[] = [
  // Flutter `Color(int)` ARGB hex — matches the captured cURL palette.
  { value: '0xFFA7CAFF', label: 'Soft Blue' },
  { value: '0xFFB6E300', label: 'Lime' },
  { value: '0xFF65D24A', label: 'Green' },
  { value: '0xFFFF9B2B', label: 'Orange' },
  { value: '0xFFEE6464', label: 'Salmon' },
  { value: '0xFFFCC001', label: 'Yellow' },
];

const APPOINTMENT_TYPES: { code: 'ROUTINE' | 'FOLLOWUP' | 'WALKIN'; display: string }[] = [
  { code: 'ROUTINE', display: 'First Time' },
  { code: 'FOLLOWUP', display: 'Follow Up' },
  { code: 'WALKIN', display: 'Walk In' },
];

export interface PracticeOption {
  practiceSetupId: string | null;
  practiceName: string | null;
}

export interface AppointmentProfileEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing row to edit, or null for Add. */
  initial: AppointmentProfileEntry | null;
  /** Full practitioner profile list (used to build the save envelope). */
  allProfiles: AppointmentProfileEntry[];
  /** Enabled locations on this practice (verbatim from GET-side echo,
   *  required envelope field on the save body). */
  enabledLocations: ApptLocation[];
  /** Practice picker source on Add mode. */
  practiceOptions: PracticeOption[];
  doctorSetupId: string;
  organizationId: string;
}

function makeBlankProfile(
  doctorSetupId: string,
  organizationId: string,
): AppointmentProfileEntry {
  return {
    // `id` will be overridden to `null` in the save body. The on-screen
    // draft uses a placeholder string to keep the type happy; the
    // payload-build step replaces it.
    id: '',
    organizationId,
    practionerId: doctorSetupId, // TYPO verbatim per backend
    practiceId: '',
    profileName: '',
    location: { id: null, name: null },
    minutesDuration: 30,
    color: COLOR_PRESETS[0].value,
    reasonCode: null,
    appointmentType: {
      code: 'ROUTINE',
      definition: null,
      display: 'First Time',
    },
    roomId: '',
    billingProfileId: null,
    billingProfile: null,
    inAppBooking: false,
    isDefault: false,
    isUpdated: true,
    isPreferred: false,
    speciality: {} as AppointmentProfileEntry['speciality'],
    specialityId: '',
  };
}

export function AppointmentProfileEditDialog({
  open,
  onOpenChange,
  initial,
  allProfiles,
  enabledLocations,
  practiceOptions,
  doctorSetupId,
  organizationId,
}: AppointmentProfileEditDialogProps) {
  const isAdd = initial === null;
  const [draft, setDraft] = useState<AppointmentProfileEntry>(
    initial ?? makeBlankProfile(doctorSetupId, organizationId),
  );
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(initial ?? makeBlankProfile(doctorSetupId, organizationId));
    }
  }, [open, initial, doctorSetupId, organizationId]);

  const saveMutation = useSaveAppointmentProfile(doctorSetupId, organizationId);

  const validPractices = useMemo(
    () =>
      practiceOptions.filter(
        (p): p is { practiceSetupId: string; practiceName: string | null } =>
          !!p.practiceSetupId,
      ),
    [practiceOptions],
  );
  const validLocations = useMemo(
    () => enabledLocations.filter((l): l is ApptLocation & { id: string } => !!l.id),
    [enabledLocations],
  );

  // Map GET-side entry → save-body entry. Edit rows mirror `id` to
  // `referenceId`; Add rows ship both as `null`.
  //
  // Defensive guard (per @samer PR #242 review): if a source row
  // arrives without a valid id (stale cache, malformed wire shape, or
  // a row that got into the React state via an unusual path), ship
  // `referenceId: null` rather than `""`. The backend's Guid parser
  // accepts `null` for new rows and rejects `""` — a silent empty-
  // string passthrough would cause a 400 that's hard to trace. We
  // also ship `id: null` in that case so the backend treats the row
  // as a new-row insert (same as the explicit Add path) rather than
  // failing on an unparseable id.
  const toSaveEntry = (
    e: AppointmentProfileEntry,
    isAddEntry: boolean,
    isUpdated: boolean,
  ): SaveAppointmentProfileEntry => {
    const hasValidId = !isAddEntry && !!e.id;
    return {
      ...e,
      id: hasValidId ? e.id : null,
      referenceId: hasValidId ? e.id : null,
      // Ensure the persisted colour is Flutter ARGB — a CSS `#RRGGBB` (e.g.
      // from a loaded legacy profile) crashes the Flutter calendar card.
      // Presets are already ARGB, so this is a no-op guard for them.
      color: cssColorToFlutter(e.color) ?? e.color,
      isUpdated,
    };
  };

  const handleSave = () => {
    // Build the FULL list. The edited (or added) row carries
    // isUpdated: true; preserved rows carry false. Mirrors Flutter
    // EnableProviderScheduleEvent dispatch.
    const preserved = allProfiles
      .filter((p) => p.id !== draft.id)
      .map((p) => toSaveEntry(p, false, false));
    const target = toSaveEntry(draft, isAdd, true);
    const body: SaveAppointmentProfileBody = {
      createAppointmentProfileRequest: isAdd
        ? [...preserved, target]
        : [
            ...allProfiles.map((p) =>
              p.id === draft.id ? target : toSaveEntry(p, false, false),
            ),
          ],
      isDeleted: [],
      enabledLocation: enabledLocations,
    };
    saveMutation.mutate(body, {
      onSuccess: () => onOpenChange(false),
    });
  };

  const handleDelete = () => {
    if (!draft.id) return;
    const remaining = allProfiles
      .filter((p) => p.id !== draft.id)
      .map((p) => toSaveEntry(p, false, false));
    const body: SaveAppointmentProfileBody = {
      createAppointmentProfileRequest: remaining,
      isDeleted: [draft.id],
      enabledLocation: enabledLocations,
    };
    saveMutation.mutate(body, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        onOpenChange(false);
      },
    });
  };

  const setField = <K extends keyof AppointmentProfileEntry>(
    key: K,
    value: AppointmentProfileEntry[K],
  ) => setDraft({ ...draft, [key]: value });

  const setLocationById = (id: string) => {
    const found = validLocations.find((l) => l.id === id);
    if (found) setField('location', found);
  };

  const setAppointmentTypeByCode = (code: string) => {
    const found = APPOINTMENT_TYPES.find((t) => t.code === code);
    if (found) {
      setField('appointmentType', {
        code: found.code,
        definition: null,
        display: found.display,
      });
    }
  };

  const canSave =
    !saveMutation.isPending &&
    !!draft.profileName.trim() &&
    !!draft.practiceId &&
    !!draft.location.id &&
    draft.minutesDuration > 0;

  return (
    <>
      <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={isAdd ? 'Add Appointment Profile' : 'Edit Appointment Profile'}
      size="lg"
      footer={<><div className="flex items-center gap-2">
              <Button type="button" disabled={!canSave} onClick={handleSave}>
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
                disabled={saveMutation.isPending}
              >
                Cancel
              </Button>
            </div>
            {!isAdd && (
              <Button
                type="button"
                variant="tertiary"
                disabled={saveMutation.isPending}
                className="text-status-error"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete
              </Button>
            )}</>}
      footerAlign="between"
    >
          

          <div className="space-y-4 py-2 max-h-overlay-body overflow-y-auto scrollbar-hide">
            <div>
              <FieldLabel stacked>
                Profile name <span className="text-status-error">*</span>
              </FieldLabel>
              <TextField
                value={draft.profileName}
                placeholder="e.g. First appointment, Follow up"
                maxLength={120}
                onChange={(e) => setField('profileName', e.target.value)}
              />
            </div>

            {isAdd ? (
              <div>
                <FieldLabel stacked>
                  Practice <span className="text-status-error">*</span>
                </FieldLabel>
                {validPractices.length === 0 ? (
                  <p className="text-sm text-text-muted">
                    No practices assigned to this practitioner.
                  </p>
                ) : (
                  <Select
                    value={draft.practiceId || ''}
                    onChange={(v) => setField('practiceId', v ?? '')}
                    placeholder="Select practice"
                    options={validPractices.map((p) => ({
                      label: p.practiceName ?? '—',
                      value: p.practiceSetupId,
                    }))}
                  />
                )}
              </div>
            ) : (
              <div className="rounded-md bg-blue-100 px-3 py-2 text-sm">
                <p className="text-xs text-text-muted">Practice</p>
                <p className="font-semibold text-blue-700">
                  {/* Practice and speciality are fixed at profile creation
                   *  per Flutter. Preserve verbatim on Edit. Show the practice
                   *  NAME, not the raw GUID — fall back to a truncated id only
                   *  if the practice isn't in the practitioner's list. */}
                  {validPractices.find((p) => p.practiceSetupId === draft.practiceId)
                    ?.practiceName ??
                    (draft.practiceId ? `${draft.practiceId.slice(0, 8)}…` : '—')}
                </p>
                {draft.speciality?.code?.displayName && (
                  <p className="text-xs text-text-muted mt-1">
                    Speciality: {draft.speciality.code.displayName}
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel stacked>
                  Duration (min) <span className="text-status-error">*</span>
                </FieldLabel>
                <TextField
                  type="number"
                  value={draft.minutesDuration}
                  min={5}
                  max={480}
                  onChange={(e) => {
                    const v = e.target.value === '' ? 0 : Number(e.target.value);
                    if (Number.isFinite(v))
                      setField('minutesDuration', v);
                  }}
                  hideSpinButtons
                />
              </div>
              <div>
                <FieldLabel stacked>Appointment type</FieldLabel>
                <Select
                  value={draft.appointmentType?.code ?? 'ROUTINE'}
                  onChange={(v) => v && setAppointmentTypeByCode(v)}
                  options={APPOINTMENT_TYPES.map((t) => ({
                    label: t.display,
                    value: t.code,
                  }))}
                />
              </div>
            </div>

            <div>
              <FieldLabel stacked>
                Location <span className="text-status-error">*</span>
              </FieldLabel>
              {validLocations.length === 0 ? (
                <p className="text-sm text-text-muted">
                  No enabled locations on this practice.
                </p>
              ) : (
                <Select
                  value={draft.location.id ?? ''}
                  onChange={(v) => v && setLocationById(v)}
                  placeholder="Select location"
                  options={validLocations.map((l) => ({
                    label: l.name ?? '—',
                    value: l.id ?? '',
                  }))}
                />
              )}
            </div>

            <div>
              <FieldLabel stacked>Color</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {COLOR_PRESETS.map((c) => {
                  const selected = draft.color === c.value;
                  const cssColor = flutterColorToCss(c.value) ?? 'var(--color-status-info-bg)';
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setField('color', c.value)}
                      title={c.label}
                      aria-label={c.label}
                      aria-pressed={selected}
                      className={
                        (selected
                          ? 'inline-flex h-8 w-8 items-center justify-center rounded-full ring-2 ring-brand-primary ring-offset-2'
                          : 'inline-flex h-8 w-8 items-center justify-center rounded-full hover:ring-2 hover:ring-border-hairline-strong') +
                        ' bg-[var(--pa-swatch-color)]'
                      }
                      style={{ '--pa-swatch-color': cssColor } as CSSProperties}
                    />
                  );
                })}
              </div>
            </div>

            <div className="border-t border-border-hairline pt-3 space-y-2">
              <ToggleRow
                label="In-app booking"
                checked={draft.inAppBooking}
                onChange={(v) => setField('inAppBooking', v)}
              />
              <ToggleRow
                label="Default profile"
                checked={draft.isDefault}
                onChange={(v) => setField('isDefault', v)}
              />
              <ToggleRow
                label="Preferred profile"
                checked={draft.isPreferred}
                onChange={(v) => setField('isPreferred', v)}
              />
            </div>

            {!isAdd && (
              <div className="border-t border-border-hairline pt-3 space-y-2">
                <p className="text-sm font-semibold text-text-default">
                  Limitations of this editor
                </p>
                <ul className="text-xs text-text-muted list-disc pl-4 space-y-1">
                  <li>
                    <strong className="font-semibold text-text-default">
                      Speciality
                    </strong>{' '}
                    cannot be changed here — it&apos;s preserved from
                    the source row.
                  </li>
                  <li>
                    <strong className="font-semibold text-text-default">
                      Room, reason code, and billing profile
                    </strong>{' '}
                    are preserved verbatim.
                  </li>
                  <li>
                    Dedicated edit surfaces for these fields ship in
                    follow-up PRs.
                  </li>
                </ul>
              </div>
            )}
            {isAdd && (
              <div className="border-t border-border-hairline pt-3 space-y-2">
                <p className="text-sm font-semibold text-text-default">
                  Limitations of this Add flow
                </p>
                <ul className="text-xs text-text-muted list-disc pl-4 space-y-1">
                  <li>
                    <strong className="font-semibold text-text-default">
                      Speciality and room
                    </strong>{' '}
                    must be picked from existing practitioner records
                    before save.
                  </li>
                  <li>
                    Full picker surfaces for speciality / room /
                    reason code / billing profile ship in follow-ups.
                  </li>
                </ul>
              </div>
            )}
          </div>

          
        </Modal>

      <ModalYesNo
      open={confirmDeleteOpen}
      onCancel={() => setConfirmDeleteOpen(false)}
      title="Delete this appointment profile?"
      message="The profile will be archived and removed from this
              practitioner's schedule. Existing appointments booked
              from it are unaffected."
      cancelLabel="Cancel"
      confirmLabel={saveMutation.isPending ? 'Deleting…' : 'Delete'}
      onConfirm={handleDelete}
      loading={saveMutation.isPending}
    />
    </>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-text-default">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={label} />
    </div>
  );
}
